import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calculator, Lock } from "lucide-react";
import { toolBySlug, TOOLS, SITE_URL } from "@/lib/tools";
import { useSeo } from "@/lib/useSeo";
import { useAuth } from "@/context/AuthContext";
import { fetchGuide } from "@/lib/api";

function OpenCalcButton({ tool, testid }) {
  const { isAuthed, hasPro, setShowAuth } = useAuth();
  const navigate = useNavigate();
  const onClick = () => {
    if (!isAuthed) return setShowAuth(true);
    navigate(hasPro ? tool.route : "/pricing");
  };
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="inline-flex items-center gap-2 bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] px-5 py-3 mono text-xs tracking-widest uppercase font-bold transition-colors"
    >
      <Calculator className="w-4 h-4" /> Open calculator
    </button>
  );
}

function Formulas({ tool }) {
  return (
    <section className="mb-10">
      <h2 className="heading text-base md:text-lg font-bold mb-4 uppercase tracking-wide">Formulas</h2>
      <div className="border border-[#334155] divide-y divide-[#334155]">
        {tool.formulas.map((f) => (
          <div key={f.label} className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 px-5 py-3 bg-[#11151c]">
            <span className="text-xs mono uppercase tracking-wider text-[#94a3b8]">{f.label}</span>
            <code className="mono text-sm text-[#f59e0b] break-all">{f.expr}</code>
          </div>
        ))}
      </div>
    </section>
  );
}

function Example({ ex }) {
  return (
    <section className="mb-10" data-testid="tool-example">
      <h2 className="heading text-base md:text-lg font-bold mb-4 uppercase tracking-wide">Worked example</h2>
      <div className="border border-[#334155] bg-[#11151c] p-5 grid md:grid-cols-2 gap-6">
        <div>
          <div className="mono text-[10px] tracking-widest text-[#64748b] mb-2">GIVEN</div>
          <ul className="space-y-1 text-sm">{ex.given.map((g) => <li key={g}>{g}</li>)}</ul>
        </div>
        <div>
          <div className="mono text-[10px] tracking-widest text-[#64748b] mb-2">WORKING</div>
          <ol className="space-y-1 text-sm mono text-[#cbd5e1]">{ex.steps.map((s) => <li key={s}>{s}</li>)}</ol>
        </div>
        <div className="md:col-span-2 border-t border-[#334155] pt-4 mono text-[#fbbf24] font-bold">{ex.result}</div>
      </div>
    </section>
  );
}

function LockedGuide({ isAuthed, onSignIn }) {
  const navigate = useNavigate();
  return (
    <section data-testid="guide-locked" className="mb-10 border border-[#f59e0b]/50 bg-[#11151c] p-8 relative overflow-hidden">
      <div className="space-y-2 mb-6 select-none blur-[3px] opacity-40" aria-hidden>
        <div className="h-4 bg-[#334155] w-3/4" /><div className="h-4 bg-[#334155] w-1/2" /><div className="h-4 bg-[#334155] w-2/3" />
      </div>
      <Lock className="w-6 h-6 text-[#f59e0b] mb-3" />
      <h2 className="heading text-base md:text-lg font-bold mb-1">Formulas &amp; worked example are locked</h2>
      <p className="text-sm text-[#94a3b8] mb-5 max-w-xl">Unlock every formula guide for $5.99/month, or get them included with Pro, Team or Lifetime.</p>
      <button
        data-testid="guide-unlock-btn"
        onClick={() => (isAuthed ? navigate("/pricing") : onSignIn())}
        className="bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] px-5 py-3 mono text-xs tracking-widest uppercase font-bold transition-colors"
      >
        {isAuthed ? "Unlock guides" : "Sign in to unlock"}
      </button>
    </section>
  );
}

function GuideContent({ slug }) {
  const { user, isAuthed, hasGuides, setShowAuth } = useAuth();
  const cacheKey = `rigcalc.guide.${slug}`;
  const [guide, setGuide] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setGuide(null);
    setFailed(false);
    if (!hasGuides) return;
    fetchGuide(slug)
      .then((g) => {
        setGuide(g);
        localStorage.setItem(cacheKey, JSON.stringify(g));
      })
      .catch(() => {
        const cached = localStorage.getItem(cacheKey);
        cached ? setGuide(JSON.parse(cached)) : setFailed(true);
      });
  }, [slug, hasGuides, cacheKey]);

  if (user === null) return null;
  if (!hasGuides) return <LockedGuide isAuthed={isAuthed} onSignIn={() => setShowAuth(true)} />;
  if (failed) return <p data-testid="guide-error" className="mb-10 text-sm text-[#94a3b8]">Could not load this guide. Check your connection and try again.</p>;
  if (!guide) return <p className="mb-10 mono text-xs text-[#64748b]">Loading guide…</p>;
  return (
    <div data-testid="guide-unlocked">
      <Formulas tool={guide} />
      <Example ex={guide.example} />
    </div>
  );
}

export default function ToolPage() {
  const { slug } = useParams();
  const tool = toolBySlug(slug);
  const jsonLd = useMemo(
    () =>
      tool && {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: tool.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
    [tool]
  );
  useSeo({
    title: tool?.metaTitle || "RIGCALC",
    description: tool?.metaDescription || "",
    canonical: `${SITE_URL}/tools/${slug}`,
    jsonLd,
  });
  if (!tool) return <Navigate to="/tools" replace />;
  const related = TOOLS.filter((t) => t.slug !== slug).slice(0, 4);

  return (
    <article data-testid="tool-page" className="max-w-4xl">
      <Link to="/tools" data-testid="tool-back-link" className="inline-flex items-center gap-1 mono text-[10px] tracking-widest uppercase text-[#94a3b8] hover:text-[#f59e0b] mb-6">
        <ArrowLeft className="w-3 h-3" /> All calculators
      </Link>
      <h1 data-testid="tool-title" className="heading text-4xl sm:text-5xl font-bold leading-tight mb-4">{tool.name}</h1>
      <p className="text-[#cbd5e1] max-w-3xl mb-6">{tool.intro}</p>
      <div className="mb-12"><OpenCalcButton tool={tool} testid="tool-open-calc-top" /></div>
      <GuideContent slug={slug} />
      <section className="mb-10" data-testid="tool-faqs">
        <h2 className="heading text-base md:text-lg font-bold mb-4 uppercase tracking-wide">FAQ</h2>
        <div className="space-y-4">
          {tool.faqs.map((f) => (
            <div key={f.q} className="border-l-2 border-[#f59e0b] pl-4">
              <h3 className="font-semibold mb-1">{f.q}</h3>
              <p className="text-sm text-[#94a3b8]">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
      <div className="border border-[#f59e0b]/40 bg-[#f59e0b]/5 p-6 mb-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="heading font-bold">Run it with your well data</div>
          <div className="text-sm text-[#94a3b8]">Field &amp; Metric units, works offline, save and share results with your crew.</div>
        </div>
        <OpenCalcButton tool={tool} testid="tool-open-calc-bottom" />
      </div>
      <div className="mono text-[10px] tracking-widest text-[#64748b] mb-3">RELATED</div>
      <div className="grid sm:grid-cols-2 gap-2 mb-6">
        {related.map((t) => (
          <Link key={t.slug} to={`/tools/${t.slug}`} data-testid={`related-${t.slug}`} className="flex items-center justify-between border border-[#334155] hover:border-[#f59e0b] px-4 py-3 text-sm transition-colors">
            {t.name} <ArrowRight className="w-3 h-3 text-[#f59e0b]" />
          </Link>
        ))}
      </div>
      <p className="text-[11px] text-[#64748b]">Reference only. Always verify against your company procedures and well-control certification.</p>
    </article>
  );
}
